import { ActionDateTimeRow } from './ActionDateTimeRow.component';
import { AnimatedTr } from './AnimatedTr.component';
import { AnimatePresence } from 'framer-motion';
import { TFunction } from 'i18next';
import { DryingHerbsContext } from 'pages/les-outils/assechement-de-l-herbe';
import {
  DryingHerbs,
  DryingHerbsAction,
  DryingHerbsActionType,
  DryingHerbsInput,
  GrassMeasureType,
} from 'pleinchamp-api-client';
import React, { ChangeEvent, ComponentPropsWithRef, FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ServiceApi } from '@api/business/api.utils';
import { AnimatedButton } from '@components/buttons/AnimatedButton/AnimatedButton.component';
import { FlatButtonVariant } from '@components/buttons/Buttons.type';
import { PlusIcon } from '@components/icons/Plus.icon';
import { Text } from '@components/Text/Text.component';
import DayjsUtils from '@date-io/dayjs';
import { formValidator, Value } from '@form/business/form.types';
import { FormValidators, useFormState } from '@form/business/useFormState.hook';
import { Checkbox } from '@form/components/Checkbox/Checkbox.component';
import { Input } from '@form/components/Input/Input.component';
import { PlcDatePicker } from '@form/components/PlcDatePicker/PlcDatePicker.component';
import { PlcForm } from '@form/components/PlcForm/PlcForm.component';
import { PlcTimePicker } from '@form/components/PlcTimePicker/PlcTimePicker.component';
import { SimpleSelect } from '@form/components/SimpleSelect/SimpleSelect.component';
import { HorizontalSeparator } from '@layout/components/HorizontalSeparator/HorizontalSeparator.component';
import { PlcButtonGroup } from '@layout/components/PlcButtonGroup/PlcButtonGroup.component';
import { useTranslation } from '@locales/useTranslation.hook';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { MeteoCityAutocomplete } from '@meteo/components/MeteoCityAutocomplete/MeteoCityAutocomplete.component';
import { StandardCity } from '@meteo/types';
import { Breakpoint, useIsBreakpointDown } from '@styles/breakpoints';
import { plcDayjs } from '@utils/date';
import classnames from 'classnames';
import { Dayjs } from 'dayjs';

import './DryingHerbsForm.scss';

type Props = Partial<ComponentPropsWithRef<typeof PlcForm>> & {
  onNewResults: (results: DryingHerbs[]) => void;
  onFormError: (errorCode: number | undefined) => void;
};

export type DryingHerbsFormAction = { id: string | number; type: DryingHerbsActionType; date?: Dayjs; time?: Dayjs };
export type DryingHerbsFormState = Omit<DryingHerbsInput, 'cuttingDate' | 'actions' | 'geopoint'> & {
  cuttingDate?: Dayjs;
  cuttingTime?: Dayjs;
  city?: StandardCity;
  intermediateActions: Partial<DryingHerbsFormAction>[];
  firstAction: DryingHerbsFormAction;
  lastAction: DryingHerbsFormAction;
};

const MAX_INTERMEDIATE_ACTION_LENGTH = 8 - 2;

function fromDateTimeToDayJs(date: DryingHerbsFormAction['date'], time: DryingHerbsFormAction['time']) {
  if (!date || !time) {
    return undefined;
  }
  return date.set('hour', time.hour()).set('minute', time.minute());
}

function fromFormActionToInputAction({ type, date, time }: DryingHerbsFormAction): DryingHerbsAction | undefined {
  const dateTime = fromDateTimeToDayJs(date, time);
  if (!dateTime) {
    return undefined;
  }
  return {
    date: dateTime.toDate(),
    type,
  };
}
/**
 * When you change the value of date or time you have to make it match both.
 * That mean if you change the object date call time you have to changed the time of the var date
 * For the object call date when you have to changed the date you have also to match the date of time.
 * If not the form validators are wrong.
 * @param date The date display in form
 * @param time The time display in form
 * @returns object : Date and Time
 */
function getDateAndTimeAdequate(date: DryingHerbsFormAction['date'], time: DryingHerbsFormAction['time']) {
  // On récupère la date précédente
  const previousDate = date?.clone();
  // On récupère la date précédente mais faisant office de "time".
  const previousTime = time?.clone();
  // On fait en sorte que la date corresponde à la date précédente même si on ne manipule que le time.
  const newTime = fromDateTimeToDayJs(previousDate, previousTime);
  // On fait en sorte que le time de la date précédente corresponde au nouveau time.
  const newDate = fromDateTimeToDayJs(previousDate, newTime);

  return { newDate, newTime };
}

function fromFormDataToInput({
  cuttingDate,
  cuttingTime,
  firstAction,
  lastAction,
  intermediateActions,
  city,
  ...rest
}: DryingHerbsFormState): DryingHerbsInput | undefined {
  const cuttingDateTime = fromDateTimeToDayJs(cuttingDate, cuttingTime);
  if (!city || !cuttingDateTime) {
    return undefined;
  }

  return {
    actions: [
      fromFormActionToInputAction(firstAction),
      ...intermediateActions.map(action => fromFormActionToInputAction(action as DryingHerbsFormAction)),
      fromFormActionToInputAction(lastAction),
    ] as DryingHerbsAction[],
    cuttingDate: cuttingDateTime.toDate(),
    // Need to destructure object : setting geopoint : city.geometry doesn't work for geolocation (PLC-2305)
    geopoint: { latitude: city.geometry.latitude, longitude: city.geometry.longitude },
    ...rest,
  };
}

function grassMeasureTypeCodeToValue(code: string | undefined, t: TFunction) {
  if (!code) return undefined;
  return { label: t(`services.dryingHerbs.cutting.quantity.${code}`), value: code };
}

function grassMeasureDensityValueCodeToValue(code: string, t: TFunction) {
  switch (code) {
    case '2': {
      return { label: t('services.dryingHerbs.cutting.quantity.density.normal'), value: code };
    }
    case '3': {
      return { label: t('services.dryingHerbs.cutting.quantity.density.high'), value: code };
    }
    default:
    case '1': {
      return { label: t('services.dryingHerbs.cutting.quantity.density.low'), value: code };
    }
  }
}

function getDefaultGrassMeasureValue(type: GrassMeasureType) {
  switch (type) {
    case GrassMeasureType.Height: {
      return { max: 200, min: 5, step: 1, unity: `services.dryingHerbs.cutting.quantity.${type}.unity` };
    }
    case GrassMeasureType.Weight: {
      return { max: 20000, min: 500, step: 100, unity: `services.dryingHerbs.cutting.quantity.${type}.unity` };
    }
    case GrassMeasureType.Density: {
      return { max: 3, min: 1, unity: '' };
    }
    default: {
      const switchTest: never = type;
      return switchTest;
    }
  }
}

function getMinMaxFromGrassMeasureType(type: GrassMeasureType): [number, number, string] {
  const { min, max, unity } = getDefaultGrassMeasureValue(type);
  return [min, max, unity];
}
function checkIfActionIsBeforePrevious(
  actionDate: DryingHerbsFormAction['date'],
  actionTime: DryingHerbsFormAction['time'],
  previousDate: DryingHerbsFormAction['date'],
  previousTime: DryingHerbsFormAction['time']
) {
  const actionDateTime = fromDateTimeToDayJs(actionDate, actionTime);
  const previousDateTime = fromDateTimeToDayJs(previousDate, previousTime);

  // if the previousDateTime is not present we do not display an error message to avoid confusion (it will be displayed anyway by the previous action)
  if (!previousDateTime) {
    return true;
  }
  return actionDateTime && actionDateTime.isBefore(previousDateTime);
}

const DryingHerbsForm: FC<Props> = ({ className, onNewResults, onFormError, ...rest }) => {
  const {
    city: { data: city, isValidating: isGeolocationLoading, error: geolocationError, setData: setSelectedCity },
  } = useContext(DryingHerbsContext);
  const isMobile = useIsBreakpointDown(Breakpoint.m);
  const [formResults, setFormResults] = useState<DryingHerbs[]>([]);
  const [intermediateActionIdCounter, setIntermediateActionIdCounter] = useState(0);

  const [oldFirstActionDate, setOldFirstActionDate] = useState({});

  const { t } = useTranslation(['common', 'services']);
  const [isCityInit, setIsCityInit] = useState(!isGeolocationLoading);
  useEffect(() => {
    if (!isCityInit && !isGeolocationLoading) {
      setIsCityInit(true);
    }
  }, [isGeolocationLoading]);

  const initialFormState: DryingHerbsFormState = useMemo(() => {
    const cuttingDate = plcDayjs().startOf('day');
    const cuttingTime = plcDayjs();
    const noon = plcDayjs().set('hour', 12);
    const firstHayingDate = noon.add(1, 'day');
    const lastSwathingDate = noon.add(2, 'day');

    const firstAction = {
      date: firstHayingDate,
      id: 'firstAction',
      time: firstHayingDate,
      type: DryingHerbsActionType.Haying,
    };

    setOldFirstActionDate(firstAction);

    return {
      city: isCityInit ? city : undefined,
      cuttingDate,
      cuttingTime,
      firstAction,
      grassMeasureType: GrassMeasureType.Density,
      grassMeasureValue: 1,
      intermediateActions: [],
      isWetGrass: true,
      lastAction: {
        date: lastSwathingDate,
        id: 'lastAction',
        time: lastSwathingDate,
        type: DryingHerbsActionType.Swathing,
      },
    };
  }, [isCityInit]);

  const cityRef = useRef<HTMLDivElement>(null);
  const cuttingDateRef = useRef<HTMLDivElement>(null);
  const grassMeasureTypeRef = useRef<HTMLDivElement>(null);
  const grassMeasureValueRef = useRef<HTMLDivElement>(null);
  const actionTableRef = useRef<HTMLTableElement>(null);

  /* eslint-disable sort-keys-fix/sort-keys-fix */
  const validators: FormValidators<DryingHerbsFormState> = {
    city: {
      method: value => [
        {
          isValid: !geolocationError,
          message: geolocationError || t('meteo.error.geolocation'),
        },
        formValidator(value, t, true).required(),
      ],
      ref: cityRef,
    },
    cuttingDate: {
      method: value => [formValidator(value, t, true).required()],
      ref: cuttingDateRef,
    },
    cuttingTime: {
      method: value => [formValidator(value, t, true).required()],
      ref: cuttingDateRef,
    },
    grassMeasureType: {
      method: value => [formValidator(value, t, true).required()],
      ref: grassMeasureTypeRef,
    },
    grassMeasureValue: {
      method: (value, formState) => [
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        formValidator(value, t, true).valueBetween(...getMinMaxFromGrassMeasureType(formState!.grassMeasureType)),
      ],
      ref: grassMeasureValueRef,
    },
    firstAction: {
      method: (value, formState) => {
        const intermediateActions = formState?.intermediateActions;

        // Controle sur les date de fauche
        const isFirstActionBeforeCuttingDate = checkIfActionIsBeforePrevious(
          value.date,
          value.time,
          formState?.cuttingDate,
          formState?.cuttingTime
        );
        // Controle que la date est antérieure à la date de la dernière action s'il n'y a pas d'action intermédiaire.
        const isLastActionBeforeFirstAction =
          intermediateActions &&
          intermediateActions.length === 0 &&
          checkIfActionIsBeforePrevious(formState?.lastAction.date, formState?.lastAction.time, value.date, value.time);

        // Controle que la date est antérieure à la première date des actions intermédiaire.
        const isFirstIntermediateActionsBeforeFirstAction =
          intermediateActions &&
          intermediateActions.length > 0 &&
          checkIfActionIsBeforePrevious(
            intermediateActions[0]?.date,
            intermediateActions[0]?.time,
            value.date,
            value.time
          );

        let message = t('services.dryingHerbs.actions.error.first-action-before-cutting-date');
        if (isLastActionBeforeFirstAction) {
          message = t('services.dryingHerbs.actions.error.action-is-after');
        }
        if (isFirstIntermediateActionsBeforeFirstAction) {
          message = t('services.dryingHerbs.actions.error.first-action-after-following-date');
        }
        const isValid =
          !isFirstActionBeforeCuttingDate &&
          !isLastActionBeforeFirstAction &&
          !isFirstIntermediateActionsBeforeFirstAction;

        return [
          {
            isValid,
            message,
          },
        ];
      },
      ref: actionTableRef,
    },
    intermediateActions: {
      method: (value, formState) => {
        if (formState?.intermediateActions.length === 0) {
          return [{ isValid: true, message: '' }];
        }
        if (!value.length || !formState?.intermediateActions.length) {
          return [{ isValid: false, message: '' }];
        }
        const { firstAction, intermediateActions } = formState;
        const { date, time, type } = value[0];

        const dateTime = fromDateTimeToDayJs(date, time);
        const index = intermediateActions.findIndex(a => {
          const intermediateDateTime = fromDateTimeToDayJs(a.date, a.time);
          return intermediateDateTime && dateTime && a.type === type && intermediateDateTime.isSame(dateTime);
        });
        const previousAction =
          index > 0 ? intermediateActions[index - 1] : { date: firstAction.date, time: firstAction.time };

        const isValid = !checkIfActionIsBeforePrevious(date, time, previousAction.date, previousAction.time);
        return [
          {
            isValid,
            message: !isValid ? t('services.dryingHerbs.actions.error.action-is-after') : '',
          },
        ];
      },
      ref: actionTableRef,
    },
    lastAction: {
      method: (value, formState) => {
        return [
          {
            isValid: !checkIfActionIsBeforePrevious(
              value.date,
              value.time,
              formState?.intermediateActions.length
                ? formState.intermediateActions[formState.intermediateActions.length - 1].date
                : formState?.firstAction.date,
              formState?.intermediateActions.length
                ? formState.intermediateActions[formState.intermediateActions.length - 1].time
                : formState?.firstAction.time
            ),
            message: t('services.dryingHerbs.actions.error.action-is-after'),
          },
        ];
      },
      ref: actionTableRef,
    },
  };

  const {
    formData,
    setFormField,
    isSubmitted,
    ErrorMessageBasedOnCode,
    plcFormProps,
    disabledSubmitButton,
    submitCallbackErrorCode,
  } = useFormState({
    initialFormState,
    useNavigationWarn: true,
    validators,
  });
  const {
    cuttingDate,
    cuttingTime,
    grassMeasureType,
    grassMeasureValue,
    isWetGrass,
    firstAction,
    lastAction,
    intermediateActions,
  } = formData;

  function handleAddActionClick() {
    // Si on avait ajouter une action intermediaire et modifier la first action on a une erreur.
    // Si on delete l'action intermediaire la date dans le state first action n'a pas changé
    // Si lors du prochain ajout de l'intermediate action il faudra avant changé la first action car il n'y a plus lieu d'avoir une erreur
    // Pour FirstAction on s'assure que les dates (et temps) coincident
    if (!intermediateActions.length) {
      const { date, time } = oldFirstActionDate as DryingHerbsFormAction;
      const newDateAndTime = getDateAndTimeAdequate(date, time);

      firstAction.date = newDateAndTime?.newDate;
      firstAction.time = newDateAndTime?.newTime;
    }

    // On récupère la date précédente
    const previousDate = intermediateActions.length
      ? intermediateActions[intermediateActions.length - 1].date?.clone()
      : firstAction.date?.clone();
    // On récupère la date précédente mais faisant office de "time".
    const previousTime = intermediateActions.length
      ? intermediateActions[intermediateActions.length - 1].time?.clone()
      : firstAction.time?.clone();

    const dateAndTime = getDateAndTimeAdequate(previousDate, previousTime);

    setFormField('intermediateActions', [
      ...intermediateActions,
      {
        id: intermediateActionIdCounter,
        type: DryingHerbsActionType.Swathing,
        date: dateAndTime?.newDate?.add(1, 'minute'),
        time: dateAndTime?.newTime?.add(1, 'minute'),
      },
    ]);
    setIntermediateActionIdCounter(intermediateActionIdCounter + 1);
  }
  const handleDeleteActionClick = (index: number) => () => {
    const newIntermediateActions = [...intermediateActions];
    newIntermediateActions.splice(index, 1);
    setFormField('intermediateActions', newIntermediateActions);
  };

  function callDryingHerbsApi(input?: DryingHerbsInput) {
    if (input) {
      return ServiceApi.getDryingHerbs(input)
        .then(r => r.data)

        .then(setFormResults);
    }
    return Promise.resolve();
  }
  function onSubmitCallback() {
    const input = fromFormDataToInput(formData);
    return callDryingHerbsApi(input);
  }
  useEffect(() => {
    if (!isCityInit) {
      return;
    }
    callDryingHerbsApi(fromFormDataToInput(initialFormState)).catch(() => undefined);
  }, [initialFormState, isCityInit]);

  useEffect(() => {
    onNewResults(formResults);
  }, [formResults]);
  useEffect(() => {
    onFormError(submitCallbackErrorCode);
  }, [submitCallbackErrorCode]);

  useEffect(() => {
    if (!isCityInit) {
      return;
    }
    setFormField('city', city);
  }, [city, isCityInit]);
  useEffect(() => {
    const newDefaultValue = getDefaultGrassMeasureValue(grassMeasureType);
    setFormField('grassMeasureValue', newDefaultValue.min);
  }, [grassMeasureType]);

  const grassMeasureTypeOptions: Value[] = useMemo(
    () =>
      [GrassMeasureType.Density, GrassMeasureType.Height, GrassMeasureType.Weight].map(
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        code => grassMeasureTypeCodeToValue(code, t)!
      ),
    []
  );
  const grassMeasureDensityValueOptions: Value[] = useMemo(
    () => [1, 2, 3].map(code => grassMeasureDensityValueCodeToValue(code.toString(), t)),
    []
  );
  const grassMeasureValueSpecificProps = useMemo(() => {
    const { min, max, step, unity } = getDefaultGrassMeasureValue(grassMeasureType);
    const rightIcon = <Text i18nKey={unity} variant="p" />;
    return { max, min, rightIcon, step };
  }, [grassMeasureType]);

  const rootClass = classnames('drying-herbs-form', className);
  const fieldsetClassName = 'plc-mb-l';
  const rowLayoutClassName = 'flex column row-up-m';
  const rowSpacingClassName = 'plc-mb-m plc-mb-up-m-basis';
  const rowElementClassName = 'plc-mb-m plc-mb-up-m-0 plc-mr-up-m-basis';

  function onChangeDate(date: Dayjs | null) {
    setFormField('cuttingDate', date || undefined);
  }
  function onChangeTime(time?: Dayjs) {
    setFormField('cuttingTime', time);
  }

  function onSelectGrassMeasureType(val?: Value) {
    setFormField('grassMeasureType', val ? (val.value as GrassMeasureType) : GrassMeasureType.Density);
  }

  function onSelectGrassMeasureValue(val?: Value) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    setFormField('grassMeasureValue', Number(val!.value));
  }
  function onChangeGrassMeasureValue(event: ChangeEvent<HTMLInputElement>) {
    setFormField('grassMeasureValue', Number(event.target.value));
  }
  function onCheckIsWetGrass(val: boolean) {
    setFormField('isWetGrass', val);
  }
  const onChangeActionDateTimeRow = (formField: keyof DryingHerbsFormState) => (
    newAction: Partial<DryingHerbsFormAction>
  ) => {
    if (newAction.date && newAction.time) {
      // Pour FirstAction et LastAction on s'assure que les dates (et temps) coincident
      const newDateAndTime = getDateAndTimeAdequate(newAction.date, newAction.time);

      const newFirstActionValue = {
        id: newAction.id,
        type: newAction.type,
        date: newDateAndTime?.newDate,
        time: newDateAndTime?.newTime,
      };
      // On stock la saisie de l'user même si c'est faut pour garder le message d'erreur lors d'un ajout d'action intermediaire ou enlever le msg lors d'un suppression
      if (formField === 'firstAction') {
        setOldFirstActionDate(newFirstActionValue);
      }

      setFormField(formField, newFirstActionValue as DryingHerbsFormAction);
    }
  };

  const onChangeIntermediateActions = (index: number) => (newAction: Partial<DryingHerbsFormAction>) => {
    const newIntermediateActions = [...intermediateActions];
    // Pour FirstAction et LastAction on s'assure que les dates (et temps) coincident
    const newDateAndTime = getDateAndTimeAdequate(newAction.date, newAction.time);

    const newIntermediateAction = {
      id: newAction.id,
      type: newAction.type,
      date: newDateAndTime?.newDate,
      time: newDateAndTime?.newTime,
    };

    newIntermediateActions[index] = newIntermediateAction;
    setFormField('intermediateActions', newIntermediateActions);
  };

  const intermediateActionsComponents = useMemo(
    () =>
      intermediateActions.map((action, i) => (
        <ActionDateTimeRow
          key={action.id}
          isHintVisible={isSubmitted}
          isSubmitted={isSubmitted}
          minDate={cuttingDate}
          onChange={onChangeIntermediateActions(i)}
          onDelete={handleDeleteActionClick(i)}
          validators={validators.intermediateActions?.method([action], formData)}
          value={action}
        />
      )),
    [intermediateActions, firstAction, lastAction]
  );
  return (
    <MuiPickersUtilsProvider libInstance={plcDayjs} locale="fr" utils={DayjsUtils}>
      <PlcForm className={rootClass} onSubmitCallback={onSubmitCallback} {...plcFormProps} {...rest}>
        <MeteoCityAutocomplete
          ref={cityRef}
          className="plc-mb-xl"
          isHintVisible={isSubmitted || Boolean(geolocationError)}
          onSelect={setSelectedCity}
          validators={validators.city?.method(city)}
        />
        <fieldset className={fieldsetClassName}>
          <legend>
            <Text className="plc-mb-basis" i18nKey="services.dryingHerbs.cutting.title" tag="h2" variant="h4" />
          </legend>
          <div className={classnames(rowLayoutClassName, rowSpacingClassName)}>
            <PlcDatePicker
              ref={cuttingDateRef}
              className={rowElementClassName}
              isHintVisible={isSubmitted}
              labelI18n="services.dryingHerbs.cutting.date"
              maxDate={plcDayjs().add(4, 'day')}
              minDate={plcDayjs().subtract(2, 'day')}
              onChange={onChangeDate}
              validators={validators.cuttingDate?.method(cuttingDate)}
              value={cuttingDate}
              variant={isMobile ? 'full-width' : undefined}
            />
            <PlcTimePicker
              isHintVisible={isSubmitted}
              labelI18n="services.dryingHerbs.cutting.time"
              onChange={onChangeTime}
              validators={validators.cuttingTime?.method(cuttingTime)}
              value={cuttingTime}
              variant={isMobile ? 'full-width' : 'small-input'}
            />
          </div>
          <Text className="plc-mb-s" i18nKey="services.dryingHerbs.cutting.quantity.title" variant="h6" />
          <div className={classnames(rowLayoutClassName, rowSpacingClassName)}>
            <SimpleSelect
              ref={grassMeasureTypeRef}
              className={rowElementClassName}
              isHintVisible={isSubmitted}
              onSelect={onSelectGrassMeasureType}
              options={grassMeasureTypeOptions}
              validators={validators.grassMeasureType?.method(grassMeasureType)}
              value={grassMeasureTypeCodeToValue(grassMeasureType, t)}
              variant={isMobile ? 'full-width' : undefined}
            />
            <div ref={grassMeasureValueRef} className="flex-1">
              {grassMeasureType === GrassMeasureType.Density ? (
                <SimpleSelect
                  isHintVisible={isSubmitted}
                  onSelect={onSelectGrassMeasureValue}
                  options={grassMeasureDensityValueOptions}
                  validators={validators.grassMeasureValue?.method(grassMeasureValue, formData)}
                  value={grassMeasureDensityValueCodeToValue(grassMeasureValue.toString(), t)}
                  variant={isMobile ? 'full-width' : 'small-input'}
                />
              ) : (
                <Input
                  isHintVisible={isSubmitted}
                  onChange={onChangeGrassMeasureValue}
                  style={{ textAlign: 'right' }}
                  type="number"
                  validators={validators.grassMeasureValue?.method(grassMeasureValue, formData)}
                  value={grassMeasureValue.toString()}
                  variant={isMobile ? 'full-width' : 'small-input'}
                  {...grassMeasureValueSpecificProps}
                />
              )}
            </div>
          </div>
          <Checkbox
            checked={isWetGrass}
            label={t('services.dryingHerbs.cutting.wet-grass')}
            onCheck={onCheckIsWetGrass}
          />
        </fieldset>
        <HorizontalSeparator className="plc-mb-l" />

        <fieldset className={classnames(fieldsetClassName, 'flex', 'column')}>
          <legend>
            <Text className="plc-mb-basis" i18nKey="services.dryingHerbs.actions.title" tag="h2" variant="h4" />
          </legend>
          <table ref={actionTableRef} className="actions-table width-100">
            <tbody>
              <ActionDateTimeRow
                actionDisabled={true}
                isHintVisible={isSubmitted}
                isSubmitted={isSubmitted}
                minDate={cuttingDate}
                onChange={onChangeActionDateTimeRow('firstAction')}
                validators={validators.firstAction?.method(firstAction, formData)}
                value={firstAction}
              />

              <AnimatePresence initial={false}>
                {intermediateActionsComponents}
                {intermediateActions.length < MAX_INTERMEDIATE_ACTION_LENGTH && (
                  <AddActionButton onClick={handleAddActionClick} />
                )}
              </AnimatePresence>

              <ActionDateTimeRow
                actionDisabled={true}
                isHintVisible={isSubmitted}
                isSubmitted={isSubmitted}
                minDate={firstAction.date}
                onChange={onChangeActionDateTimeRow('lastAction')}
                validators={validators.lastAction?.method(lastAction, formData)}
                value={lastAction}
              />
            </tbody>
          </table>
        </fieldset>
        {!isMobile && <HorizontalSeparator className="plc-mb-l" />}

        <PlcButtonGroup
          buttons={[
            {
              disabled: disabledSubmitButton || isGeolocationLoading,
              textI18nKey: 'common.cta.label.update',
              type: 'submit',
              variant: FlatButtonVariant.primary,
            },
          ]}
        />
        <ErrorMessageBasedOnCode className="plc-mt-m" />
      </PlcForm>
    </MuiPickersUtilsProvider>
  );
};

const AddActionButton: FC<ComponentPropsWithRef<typeof AnimatedButton>> = ({ className, ...rest }) => {
  const buttonClass = classnames(
    'plc-p-m',
    'flex',
    'space-between',
    'align-center',
    'background-ultralight',
    className
  );
  return (
    <AnimatedTr index="add-action-button">
      <td className="add-action-button" colSpan={3}>
        <AnimatedButton className={buttonClass} {...rest}>
          <Text flavour="grey" i18nKey="services.dryingHerbs.actions.add-action" variant="h6" />
          <PlusIcon />
        </AnimatedButton>
      </td>
    </AnimatedTr>
  );
};

export { DryingHerbsForm };
